import React, { useContext, useState, useEffect, useCallback } from 'react'
import { useAllMarkets } from '../services'
import { storageMode, findClosestMarketFromIp } from '../helpers'
import { growthbook } from '../services/growthbook'

export const MarketContext = React.createContext({})
export const useMarketContext = () => useContext(MarketContext)

export const MarketContextProvider = ({ children }) => {
  const [selectedMarket, _setSelectedMarket] = useState(null)
  // True once the initial market resolution has SETTLED — either read back from
  // localStorage or returned (successfully or not) from the IP lookup. Consumers
  // that must not act on a provisional `null` (the straight-to-service seed in
  // pages/book/index.js) wait on this instead of on `selectedMarket` itself,
  // which is legitimately null while ipdata.co is still being asked.
  const [marketResolved, setMarketResolved] = useState(false)
  const markets = useAllMarkets()
  const storage = storageMode('localStorage')

  // wrapper around _setSelectedMarket to update local storage
  // as well as context state.
  const setSelectedMarket = useCallback(
    (market) => {
      if (market) {      
        // once selected, save market in browser for 1 week
        let date = new Date()
        date = date.setDate(date.getDate() + 1 * 7).toString()
        storage.setItem(
          'currentMarket',
          JSON.stringify({ exp: date, id: market.id })
        )

        growthbook.setAttributes({
          ...growthbook.getAttributes(),
          market: market.shortName,
          // Boolean attribute for experiment market targeting (matches the checkout tip gate).
          fullyAdjustableTipRate: !!market.fullyAdjustableTipRate,
        })
      }

      return _setSelectedMarket(market)
    },
    [storage]
  )

  useEffect(() => {
    if (!markets || !setSelectedMarket) {
      return
    }

    let now = new Date()
    now = now.getTime()

    // Settle marketResolved whether the IP lookup fulfils or rejects — a
    // rejected lookup must not leave consumers waiting (or surface as an
    // unhandled rejection); the helper already falls back to markets[0].
    const settle = () => setMarketResolved(true)

    // check for market in local storage
    let savedMarket
    try {
      savedMarket = JSON.parse(storage.getItem('currentMarket'))
    } catch (error) {
      console.warn('error parsing saved market', error)
    }

    if (savedMarket) {
      if (Number(savedMarket.exp) < now) {
        // if time expired, remove local storage, find market based on IP address
        storage.removeItem('currentMarket')
        findClosestMarketFromIp(markets, setSelectedMarket).then(settle, settle)
      } else {
        // find market by matching ID and set
        savedMarket = markets.find((m) => m.id === savedMarket.id)
        setSelectedMarket(savedMarket)
        setMarketResolved(true)
      }
    } else {
      // Async: the IP lookup is a separate network round-trip that can land well
      // after other startup work (e.g. GrowthBook features). Flip marketResolved
      // only once it settles so nobody seeds state from the interim null.
      findClosestMarketFromIp(markets, setSelectedMarket).then(settle, settle)
    }
  }, [markets, setSelectedMarket, storage])

  return (
    <MarketContext.Provider
      value={{ markets: markets || [], selectedMarket, setSelectedMarket, marketResolved }}
    >
      {children}
    </MarketContext.Provider>
  )
}
